import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { Observable } from 'rxjs/internal/Observable';

import { LocalStorageService } from '../../shared/service/localstorage/localstorage.service';

export interface UsuarioAutenticado {
  cpf: string;
  nome: string;
  nomeSocial?: string | null;
  email?: string;
  matricula?: string | null;
}

export interface UnidadeAutenticada {
  codigo?: string;
  sigla?: string;
  nome?: string;
  codigoOrgao?: string | null;
}

export interface PapelAutenticado {
  codigo: string;
  nome: string;
  papelPai?: string | null;
}

export interface RecursoOperacao {
  recurso: string;
  operacoes: string[];
}

export interface AuthContext {
  authenticated: boolean;
  expiresAt?: string;
  renewable?: boolean;

  usuario: UsuarioAutenticado;
  unidade?: UnidadeAutenticada | null;
  roles: string[];
  papeis: PapelAutenticado[];
  recursosOperacoes: RecursoOperacao[];
  fonteAutorizacaoRecursos?: string;
}

/**
 * Estrutura temporária salva no user_info.
 *
 * Além do novo contrato normalizado, mantém:
 * - name
 * - jsonUnidadePapeis
 *
 * Esses campos existem somente para evitar a quebra imediata dos
 * componentes que ainda utilizam o formato legado.
 */
export interface AuthContextCompativel extends AuthContext {
  name: string;
  jsonUnidadePapeis: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthContextService {
  private readonly storageKey = 'user_info';

  private readonly contextSubject:
    BehaviorSubject<AuthContext | null>;

  public readonly context$:
    Observable<AuthContext | null>;

  constructor(
    private localStorage: LocalStorageService
  ) {
    /*
     * A inicialização deve ocorrer dentro do construtor.
     *
     * Nesse momento, o LocalStorageService já foi atribuído
     * pelo mecanismo de injeção de dependência do Angular.
     */
    const contextoArmazenado =
      this.recuperarContextoArmazenado();

    this.contextSubject =
      new BehaviorSubject<AuthContext | null>(
        contextoArmazenado
      );

    this.context$ =
      this.contextSubject.asObservable();
  }

  /**
   * Recebe o contexto obtido pelo AuthenticationService,
   * normaliza, mantém em memória e salva a compatibilidade.
   */
  public definirContexto(
    contexto: AuthContext
  ): void {
    const contextoNormalizado =
      this.normalizarContexto(contexto);

    this.contextSubject.next(
      contextoNormalizado
    );

    this.salvarContextoCompativel(
      contextoNormalizado
    );
  }

  /**
   * Retorna o contexto atual de forma síncrona.
   */
  public getContexto(): AuthContext | null {
    return this.contextSubject.value;
  }

  /**
   * Retorna o usuário autenticado.
   */
  public getUsuario(): UsuarioAutenticado | null {
    return this.contextSubject.value?.usuario ?? null;
  }

  /**
   * Retorna a unidade autenticada.
   */
  public getUnidade(): UnidadeAutenticada | null {
    return this.contextSubject.value?.unidade ?? null;
  }

  /**
   * Retorna os papéis do usuário.
   */
  public getPapeis(): PapelAutenticado[] {
    return this.contextSubject.value?.papeis ?? [];
  }

  /**
   * Retorna os códigos dos papéis.
   */
  public getCodigosPapeis(): string[] {
    return this.getPapeis()
      .map((papel: PapelAutenticado) =>
        papel.codigo
      )
      .filter((codigo: string) =>
        !!codigo
      );
  }

  /**
   * Retorna os nomes dos papéis separados por vírgula.
   * Pode ser utilizado nos relatórios PDF.
   */
  public getNomesPapeis(): string {
    return this.getPapeis()
      .map((papel: PapelAutenticado) =>
        papel.nome?.trim()
      )
      .filter(
        (nome: string | undefined): nome is string =>
          !!nome
      )
      .join(', ');
  }

  /**
   * Verifica se o usuário possui pelo menos um
   * dos papéis informados.
   *
   * Exemplo:
   * hasPapel('PRO_CHE', 'PRO', 'DESENV001')
   */
  public hasPapel(
    ...codigosPermitidos: string[]
  ): boolean {
    if (!codigosPermitidos.length) {
      return false;
    }

    const codigosNormalizados =
      codigosPermitidos.map((codigo: string) =>
        this.normalizarCodigo(codigo)
      );

    return this.getPapeis().some(
      (papel: PapelAutenticado) =>
        codigosNormalizados.includes(
          this.normalizarCodigo(papel.codigo)
        )
    );
  }

  /**
   * Verifica se o usuário possui todos os
   * papéis informados.
   */
  public hasTodosPapeis(
    ...codigosObrigatorios: string[]
  ): boolean {
    if (!codigosObrigatorios.length) {
      return false;
    }

    const papeisUsuario =
      this.getCodigosPapeis().map(
        (codigo: string) =>
          this.normalizarCodigo(codigo)
      );

    return codigosObrigatorios.every(
      (codigo: string) =>
        papeisUsuario.includes(
          this.normalizarCodigo(codigo)
        )
    );
  }

  /**
   * Verifica se o usuário possui pelo menos
   * uma das roles informadas.
   *
   * Exemplo:
   * hasRole('ADM_SEG', 'DESENV001')
   */
  public hasRole(
    ...rolesPermitidas: string[]
  ): boolean {
    if (!rolesPermitidas.length) {
      return false;
    }

    const rolesUsuario =
      (
        this.contextSubject.value?.roles ?? []
      ).map((role: string) =>
        this.normalizarCodigo(role)
      );

    return rolesPermitidas.some(
      (role: string) =>
        rolesUsuario.includes(
          this.normalizarCodigo(role)
        )
    );
  }

  /**
   * Verifica se existe uma operação associada
   * a determinado recurso.
   */
  public hasRecursoOperacao(
    recurso: string,
    operacao?: string
  ): boolean {
    const recursos =
      this.contextSubject.value
        ?.recursosOperacoes ?? [];

    return recursos.some(
      (item: RecursoOperacao) => {
        const recursoItem =
          this.normalizarCodigo(
            item.recurso
          );

        if (
          recursoItem !==
          this.normalizarCodigo(recurso)
        ) {
          return false;
        }

        if (!operacao) {
          return true;
        }

        const operacoes =
          Array.isArray(item.operacoes)
            ? item.operacoes.map(
                (valor: string) =>
                  this.normalizarCodigo(valor)
              )
            : [];

        return operacoes.includes(
          this.normalizarCodigo(operacao)
        );
      }
    );
  }

  /**
   * Informa se há usuário autenticado
   * no contexto atual.
   */
  public isAuthenticated(): boolean {
    const contexto =
      this.contextSubject.value;

    return (
      contexto?.authenticated === true &&
      !!contexto.usuario?.cpf
    );
  }

  /**
   * Limpa o contexto em memória e no storage.
   */
  public limparContexto(): void {
    this.contextSubject.next(null);

    this.localStorage.removeData(
      this.storageKey
    );
  }

  /**
   * Normaliza o contrato recebido do backend.
   */
  private normalizarContexto(
    contexto: AuthContext
  ): AuthContext {
    const roles =
      this.normalizarListaStrings(
        contexto?.roles
      );

    const papeis: PapelAutenticado[] =
      Array.isArray(contexto?.papeis)
        ? contexto.papeis
            .filter(
              (papel: PapelAutenticado) =>
                !!papel
            )
            .map(
              (papel: PapelAutenticado) => ({
                codigo: papel.codigo ?? '',
                nome: papel.nome ?? '',
                papelPai:
                  papel.papelPai ?? null
              })
            )
            .filter(
              (papel: PapelAutenticado) =>
                !!papel.codigo
            )
        : [];

    const recursosOperacoes:
      RecursoOperacao[] =
      Array.isArray(
        contexto?.recursosOperacoes
      )
        ? contexto.recursosOperacoes
            .filter(
              (item: RecursoOperacao) =>
                !!item?.recurso
            )
            .map(
              (item: RecursoOperacao) => ({
                recurso: item.recurso ?? '',

                operacoes:
                  Array.isArray(
                    item.operacoes
                  )
                    ? item.operacoes.filter(
                        (operacao: string) =>
                          !!operacao
                      )
                    : []
              })
            )
        : [];

    return {
      authenticated:
        contexto?.authenticated === true,

      expiresAt:
        contexto?.expiresAt,

      renewable:
        contexto?.renewable,

      usuario: {
        cpf:
          contexto?.usuario?.cpf ?? '',

        nome:
          contexto?.usuario?.nome ?? '',

        nomeSocial:
          contexto?.usuario?.nomeSocial ??
          null,

        email:
          contexto?.usuario?.email ?? '',

        matricula:
          contexto?.usuario?.matricula ??
          null
      },

      unidade: contexto?.unidade
        ? {
            codigo:
              contexto.unidade.codigo ?? '',

            sigla:
              contexto.unidade.sigla ?? '',

            nome:
              contexto.unidade.nome ?? '',

            codigoOrgao:
              contexto.unidade.codigoOrgao ??
              null
          }
        : null,

      roles,
      papeis,
      recursosOperacoes,

      fonteAutorizacaoRecursos:
        contexto?.fonteAutorizacaoRecursos
    };
  }

  /**
   * Salva o contexto normalizado junto com
   * os campos temporários de compatibilidade.
   */
  private salvarContextoCompativel(
    contexto: AuthContext
  ): void {
    const contextoCompativel:
      AuthContextCompativel = {
      ...contexto,

      name:
        contexto.usuario?.nome ?? '',

      jsonUnidadePapeis:
        JSON.stringify({
          unidade: {
            ...(contexto.unidade ?? {}),
            papeis:
              contexto.papeis ?? []
          }
        })
    };

    this.localStorage.save(
      this.storageKey,
      JSON.stringify(
        contextoCompativel
      )
    );
  }

  /**
   * Recupera o contexto salvo no storage
   * ao iniciar a aplicação.
   *
   * Funciona com:
   * - contexto novo;
   * - contexto novo com compatibilidade;
   * - contexto legado com jsonUnidadePapeis.
   */
  private recuperarContextoArmazenado():
    AuthContext | null {
    const valorArmazenado =
      this.localStorage.get(
        this.storageKey
      );

    if (!valorArmazenado) {
      return null;
    }

    try {
      const armazenado =
        JSON.parse(
          valorArmazenado
        ) as Partial<AuthContextCompativel>;

      if (
        armazenado.usuario &&
        Array.isArray(armazenado.papeis)
      ) {
        return this.normalizarContexto(
          armazenado as AuthContext
        );
      }

      return this.converterContextoLegado(
        armazenado
      );
    } catch {
      return null;
    }
  }

  /**
   * Converte o formato antigo para
   * o contrato normalizado.
   */
  private converterContextoLegado(
    armazenado:
      Partial<AuthContextCompativel>
  ): AuthContext | null {
    if (
      !armazenado.jsonUnidadePapeis
    ) {
      return null;
    }

    try {
      const legado =
        JSON.parse(
          armazenado.jsonUnidadePapeis
        ) as {
          unidade?: UnidadeAutenticada & {
            papeis?: PapelAutenticado[];
          };
        };

      const papeis =
        Array.isArray(
          legado.unidade?.papeis
        )
          ? legado.unidade?.papeis ?? []
          : [];

      return this.normalizarContexto({
        /*
         * A presença de dados legados não confirma
         * que a sessão atual está autenticada.
         */
        authenticated: false,

        usuario: {
          cpf: '',
          nome:
            armazenado.name ?? '',
          email: '',
          matricula: null
        },

        unidade: legado.unidade
          ? {
              codigo:
                legado.unidade.codigo,

              sigla:
                legado.unidade.sigla,

              nome:
                legado.unidade.nome,

              codigoOrgao:
                legado.unidade.codigoOrgao
            }
          : null,

        roles: [],

        papeis,

        recursosOperacoes: [],

        fonteAutorizacaoRecursos:
          'compatibilidade_jsonUnidadePapeis'
      });
    } catch {
      return null;
    }
  }

  /**
   * Aceita roles como array ou string.
   *
   * Exemplos:
   * ['ADM_SEG', 'PRO']
   * 'ADM_SEG'
   * 'ADM_SEG,PRO'
   */
  private normalizarListaStrings(
    valor:
      | string[]
      | string
      | null
      | undefined
  ): string[] {
    if (Array.isArray(valor)) {
      return valor
        .map((item: string) =>
          item?.trim()
        )
        .filter(
          (
            item: string | undefined
          ): item is string =>
            !!item
        );
    }

    if (typeof valor === 'string') {
      return valor
        .split(',')
        .map((item: string) =>
          item.trim()
        )
        .filter(
          (item: string) =>
            !!item
        );
    }

    return [];
  }

  private normalizarCodigo(
    valor:
      | string
      | undefined
      | null
  ): string {
    return (valor ?? '')
      .trim()
      .toUpperCase();
  }
}